import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  actions as worksheetActions,
  DELETE_CHILD_SPACE_TYPE_FROM_PROJECT,
  DELETE_CUSTOM_SUBCATEGORY_SPACE_TYPES,
  DELETE_SPACE_TYPE_FROM_PROJECT,
  FETCH_SCENARIOS_ASSOCIATED_WITH_SELECTED_ROW,
  fetchScenariosAssociatedWithSelectedRowSuccess,
  setIsSpaceTypeDeletable,
  DELETE_SPACE_TYPE_ROWS_FROM_PROJECT,
  DELETE_CHILD_WORKSHEET_SPACE_ROWS_FROM_PROJECT,
  UPDATE_CHILD_SPACE_TYPE_POSITION,
  updateChildSpaceTypePosition,
  deleteWorksheetRow,
  DELETE_WORKSHEET_ROW,
  FETCH_SCENARIOS_ASSOCIATED_WITH_SELECTED_CATEGORY,
  fetchScenariosAssociatedWithSelectedCategorySuccess,
  DELETE_CATEGORY_ROWS_FROM_PROJECT,
  ADD_ROWS_TO_PROJECT_WORKSHEETS,
  UPDATE_WORKSHEET_CATEGORY,
} from 'spw-redux/modules/worksheet/actions';
import {
  deleteSpaceTypeFromProject as deleteSpaceTypeFromProjectApi,
  deleteChildSpaceTypeFromProject as deleteChildSpaceTypeFromProjectApi,
  deleteSpaceTypeRowsFromProject as deleteSpaceTypeRowsFromProjectApi,
  deleteChildWorksheetSpaceRowsFromProject as deleteChildWorksheetSpaceRowsFromProjectApi,
  Worksheet as worksheetApi,
} from 'spw-api';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import {
  actions as spaceTypeActions,
  bulkUpdateProjectSpaceTypes,
  FETCH_PROJECT_SPACE_TYPES_SUCCESS,
} from 'spw-redux/modules/space_types/actions';
import {
  actions as calculatorActions,
  fetchAllChildWorksheetSpacesSuccess,
  fetchCurrentWorksheet,
  populateWorksheet,
  saveWorksheet,
  saveWorksheet as saveWorksheetAction,
} from 'spw-redux/modules/calculators/actions';
import {
  IChildWorksheetSpaceType,
  IWorksheetRow,
  Scenario,
} from 'spw-redux/modules/calculators/types';
import { isStorageSupportSpace } from 'spw-components/worksheet/utils';
import { find, filter, some, take } from 'lodash';

import { APIResponse } from 'spw-redux/types';
import {
  selectAllChildWorksheetSpaces,
  selectCurrentScenario,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { IProjectSettings } from 'spw-redux/modules/settings/types';
import { selectProjectSettings } from 'spw-redux/modules/settings/selector';
import {
  projectSpaceTypes as projectSpaceTypesSelector,
  selectAllProjectSpaceTypeCategories,
  selectProjectSpaceTypeCategories,
} from 'spw-redux/modules/projects/selectors';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { reorder, replaceAtIndex } from 'spw-shared/utils';
import { INDIVIDUAL_WORKSPACES_CATEGORY } from 'spw-constants';
import { isNil } from 'ramda';
import {
  getScenariosUsingCategoryApi,
  getScenariosUsingSpaceTypeApi,
} from 'spw-api/scenario';
import {
  addRowsToProjectWorksheetsApi,
  deleteCategoryWorksheetRowsFromProjectApi,
  updateWorksheetCategoryApi,
} from 'spw-api/worksheet';
import { selectProjectSpaceTypes } from '../space_types/selectors';
import { IProjectSpaceType } from '../space_types/types';
import {
  hydrateWorksheetRows,
  reSortSpaceTypesAfterDelete,
} from '../calculators/utils';
import { PlainProgramScenario } from '../../../models/Scenario';
import Worksheet, { PlainWorksheet } from '../../../models/Worksheet';
import ProgramScenarioFactory from '../../../models/ProgramScenarioFactory';
import WorksheetRow from '../../../models/WorksheetRow';

function* buildScenariosAndWorksheetsUsingSpaceTypeOrCategory(
  apiScenarios: Scenario[]
) {
  const projectSettings: ReturnType<typeof selectProjectSettings> = yield select(
    selectProjectSettings
  );
  const projectSpaceTypes: IProjectSpaceType[] = yield select(
    projectSpaceTypesSelector
  );
  const childWorksheetSpaces: IChildWorksheetSpaceType[] = yield select(
    selectAllChildWorksheetSpaces
  );
  const projectSpaceTypesCategories: IProjectSpaceTypeCategory[] = yield select(
    selectProjectSpaceTypeCategories
  );

  const {
    scenariosUsingSpaceTypeOrCategory,
    worksheetsUsingSpaceTypeOrCategory,
  } = apiScenarios.reduce(
    (scenariosAndWorksheets, apiScenario: Scenario) => {
      const scenarios =
        scenariosAndWorksheets.scenariosUsingSpaceTypeOrCategory;

      const scenario = ProgramScenarioFactory.create({
        ...apiScenario,
        vacancyFactor: apiScenario.vacancyFactor?.toString(),
        headcountUtilization: apiScenario.headcountUtilization?.toString(),
        deskSharingRatio: apiScenario.deskSharingRatio?.toString(),
      }).toPlainObject();
      scenarios.push(scenario);

      const worksheet = new Worksheet(
        {
          ...apiScenario.worksheet,
          rows: hydrateWorksheetRows({
            projectSpaceTypes,
            rows: apiScenario.worksheet.rows,
            childWorksheetSpaces,
            projectSpaceTypesCategories,
          }),
        },
        scenario,
        projectSettings
      );
      const worksheets = {
        ...scenariosAndWorksheets.worksheetsUsingSpaceTypeOrCategory.byId,
        [worksheet.id]: worksheet,
      };

      return {
        ...scenariosAndWorksheets,
        scenariosUsingSpaceTypeOrCategory: scenarios,
        worksheetsUsingSpaceTypeOrCategory: {
          byId: worksheets,
        },
      };
    },
    {
      scenariosUsingSpaceTypeOrCategory: [],
      worksheetsUsingSpaceTypeOrCategory: { byId: {} },
    }
  );

  return {
    scenariosUsingSpaceTypeOrCategory,
    worksheetsUsingSpaceTypeOrCategory,
  };
}

function* fetchScenariosAssociatedWithSelectedCategoryCall(
  action: ReturnType<
    typeof worksheetActions.fetchScenariosAssociatedWithSelectedCategory
  >
) {
  try {
    const { spaceTypeCategoryId } = action.payload;

    const response: APIResponse<typeof getScenariosUsingCategoryApi> = yield call(
      getScenariosUsingCategoryApi,
      spaceTypeCategoryId
    );

    const {
      data: { scenarios: apiScenarios },
    } = response;

    const {
      scenariosUsingSpaceTypeOrCategory,
      worksheetsUsingSpaceTypeOrCategory,
    } = yield call(
      buildScenariosAndWorksheetsUsingSpaceTypeOrCategory,
      apiScenarios
    );

    yield put(
      fetchScenariosAssociatedWithSelectedCategorySuccess(
        scenariosUsingSpaceTypeOrCategory,
        worksheetsUsingSpaceTypeOrCategory
      )
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchScenariosAssociatedWithSelectedRow(
  action: ReturnType<
    typeof worksheetActions.fetchScenariosAssociatedWithSelectedRow
  >
) {
  try {
    const { projectId, spaceTypeId, isChild } = action.payload;

    const response: APIResponse<typeof getScenariosUsingSpaceTypeApi> = yield call(
      getScenariosUsingSpaceTypeApi,
      projectId,
      spaceTypeId,
      isChild
    );

    const {
      data: { scenarios: apiScenarios },
    } = response;

    const {
      scenariosUsingSpaceTypeOrCategory,
      worksheetsUsingSpaceTypeOrCategory,
    } = yield call(
      buildScenariosAndWorksheetsUsingSpaceTypeOrCategory,
      apiScenarios
    );

    yield put(
      fetchScenariosAssociatedWithSelectedRowSuccess(
        scenariosUsingSpaceTypeOrCategory,
        worksheetsUsingSpaceTypeOrCategory
      )
    );
    yield put(setIsSpaceTypeDeletable(true));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteSpaceTypeFromProject(
  action: ReturnType<typeof worksheetActions.deleteSpaceTypeFromProject>
) {
  try {
    const { projectSpaceTypeId } = action.payload;
    const projectSpaceTypes: IProjectSpaceType[] = yield select(
      selectProjectSpaceTypes
    );
    const deletedSpaceType = find(projectSpaceTypes, {
      id: projectSpaceTypeId,
    });
    yield call(deleteSpaceTypeFromProjectApi, projectSpaceTypeId);
    const { category: changedCategory } = deletedSpaceType;

    if (deletedSpaceType && changedCategory) {
      const spaceTypesToReSort = filter(
        projectSpaceTypes,
        (pst) =>
          pst.category?.id === changedCategory?.id &&
          !isStorageSupportSpace(pst.spaceType)
      );

      // determine if we need to resort, because some of the space types have a previousId
      if (some(spaceTypesToReSort, 'previousId')) {
        // if so, then resort
        const { projectSpaceTypesToUpdate } = reSortSpaceTypesAfterDelete(
          spaceTypesToReSort,
          deletedSpaceType
        );
        yield put(bulkUpdateProjectSpaceTypes(projectSpaceTypesToUpdate));
        return;
      }
    }
    // if we don't refetch after the bulk update, refetch here
    yield put(spaceTypeActions.fetchProjectSpaceTypes());
    yield put(calculatorActions.fetchAllChildWorksheetSpaces());
    yield take(FETCH_PROJECT_SPACE_TYPES_SUCCESS);
    yield put(fetchCurrentWorksheet());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteChildSpaceTypeFromProject(
  action: ReturnType<typeof worksheetActions.deleteChildSpaceTypeFromProject>
) {
  try {
    const { childSpaceTypeId } = action.payload;

    yield call(deleteChildSpaceTypeFromProjectApi, childSpaceTypeId);

    yield put(calculatorActions.fetchAllChildWorksheetSpaces());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteCustomSubcategorySpaceTypes(
  action: ReturnType<typeof worksheetActions.deleteCustomSubcategorySpaceTypes>
) {
  try {
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    const apiWorksheet = Worksheet.toAPIWorksheet(worksheet);
    const projectSettings: IProjectSettings = yield select(
      selectProjectSettings
    );

    const { projectSpaceTypes } = action.payload;
    const projectSpaceTypeIds = projectSpaceTypes.map(
      (projectSpaceType: IProjectSpaceType) => ({ id: projectSpaceType.id })
    );

    const { rows } = apiWorksheet;
    const deletedSpaceTypesMap = projectSpaceTypeIds.reduce(
      (map: Record<string, number>, projectSpaceTypeId: { id: number }) => {
        return {
          ...map,
          [projectSpaceTypeId.id.toString()]: projectSpaceTypeId.id,
        };
      },
      {}
    );

    const worksheetRows = rows
      .slice()
      .filter(
        (row: IWorksheetRow) => !deletedSpaceTypesMap[row.projectSpaceType.id]
      );

    const newAPIWorksheet = {
      ...apiWorksheet,
      rows: worksheetRows,
    };

    const newWorksheet: Worksheet = new Worksheet(
      newAPIWorksheet,
      currentScenario,
      projectSettings
    );

    yield put(populateWorksheet(newWorksheet.toPlainObject()));
    yield put(saveWorksheetAction());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteCategoryRowsFromProjectCall(
  action: ReturnType<typeof worksheetActions.deleteCategoryRowsFromProject>
) {
  try {
    const { spaceTypeCategoryId } = action.payload;
    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    const response: APIResponse<typeof deleteCategoryWorksheetRowsFromProjectApi> = yield call(
      deleteCategoryWorksheetRowsFromProjectApi,
      spaceTypeCategoryId
    );

    const {
      data: { deletedRows },
    } = response;

    const deletedRowsById: Record<number, IWorksheetRow> = (
      deletedRows || []
    ).reduce((map: Record<number, IWorksheetRow>, row: IWorksheetRow) => {
      return {
        ...map,
        [row.id]: row,
      };
    }, {});

    const { rows } = worksheet;
    const newRows = rows.filter((row) => isNil(deletedRowsById[row.id]));

    yield put(populateWorksheet(Worksheet.recalculate(worksheet, newRows)));
    yield put(saveWorksheet());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteSpaceTypeRowsFromProject(
  action: ReturnType<typeof worksheetActions.deleteSpaceTypeRowsFromProject>
) {
  try {
    const { projectSpaceTypeId } = action.payload;
    yield call(deleteSpaceTypeRowsFromProjectApi, projectSpaceTypeId);
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteChildWorksheetSpaceRowsFromProject(
  action: ReturnType<
    typeof worksheetActions.deleteChildWorksheetSpaceRowsFromProject
  >
) {
  try {
    const { childWorksheetSpaceId } = action.payload;
    yield call(
      deleteChildWorksheetSpaceRowsFromProjectApi,
      childWorksheetSpaceId
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateChildSpaceTypePositionCall(
  action: ReturnType<typeof updateChildSpaceTypePosition>
) {
  const originalChildSpaceTypes: IChildWorksheetSpaceType[] = yield select(
    selectAllChildWorksheetSpaces
  );
  const worksheet: PlainWorksheet = yield select(selectWorksheet);
  const originalRows = worksheet.rows;

  try {
    const {
      childSpaceTypeId,
      previousChildSpaceTypeId,
      nextChildSpaceTypeId,
    } = action.payload;

    const childWorksheetSpaceTypes: IChildWorksheetSpaceType[] = originalChildSpaceTypes.slice();
    const rows = originalRows.slice();

    const sourceIndex = childWorksheetSpaceTypes.findIndex(
      (cwst: IChildWorksheetSpaceType) => cwst.id === childSpaceTypeId
    );
    const sourceChildRowIndex = rows.findIndex(
      (row: WorksheetRow) =>
        row.spaceId === childWorksheetSpaceTypes[sourceIndex].id && !row.removed
    );

    let newRows = [];
    let orderedChildWorksheetSpaceTypes = [];

    const previousChildSpaceTypeIndex = previousChildSpaceTypeId
      ? childWorksheetSpaceTypes.findIndex(
          (cwst) => cwst.id === previousChildSpaceTypeId
        )
      : -1;

    const nextChildSpaceTypeIndex = nextChildSpaceTypeId
      ? childWorksheetSpaceTypes.findIndex(
          (cwst) => cwst.id === nextChildSpaceTypeId
        )
      : 0;

    if (previousChildSpaceTypeIndex >= 0) {
      if (previousChildSpaceTypeIndex < sourceIndex) {
        orderedChildWorksheetSpaceTypes = reorder(
          childWorksheetSpaceTypes,
          sourceIndex,
          previousChildSpaceTypeIndex + 1
        );
      } else {
        orderedChildWorksheetSpaceTypes = reorder(
          childWorksheetSpaceTypes,
          sourceIndex,
          previousChildSpaceTypeIndex
        );
      }
    } else if (nextChildSpaceTypeIndex >= 0) {
      orderedChildWorksheetSpaceTypes = reorder(
        childWorksheetSpaceTypes,
        sourceIndex,
        nextChildSpaceTypeIndex
      );
    }

    const previousChildRowIndex =
      previousChildSpaceTypeIndex >= 0
        ? rows.findIndex(
            (row: WorksheetRow) =>
              row.spaceId ===
                childWorksheetSpaceTypes[previousChildSpaceTypeIndex].id &&
              !row.removed
          )
        : -1;

    const nextChildRowIndex =
      nextChildSpaceTypeIndex >= 0
        ? rows.findIndex(
            (row: WorksheetRow) =>
              row.spaceId ===
                childWorksheetSpaceTypes[nextChildSpaceTypeIndex].id &&
              !row.removed
          )
        : -1;

    if (previousChildRowIndex >= 0) {
      if (previousChildRowIndex < sourceChildRowIndex) {
        newRows = reorder(rows, sourceChildRowIndex, previousChildRowIndex + 1);
      } else {
        newRows = reorder(rows, sourceChildRowIndex, previousChildRowIndex);
      }
    } else if (nextChildRowIndex >= 0) {
      newRows = reorder(rows, sourceChildRowIndex, nextChildRowIndex);
    }

    yield put(populateWorksheet(Worksheet.recalculate(worksheet, newRows)));

    yield put(
      fetchAllChildWorksheetSpacesSuccess(orderedChildWorksheetSpaceTypes)
    );

    yield call(
      worksheetApi.updateChildSpaceTypePosition,
      childSpaceTypeId,
      previousChildSpaceTypeId,
      nextChildSpaceTypeId
    );
  } catch (e) {
    yield put(fetchAllChildWorksheetSpacesSuccess(originalChildSpaceTypes));
    yield put(
      populateWorksheet(Worksheet.recalculate(worksheet, originalRows))
    );
    yield put(handleError(e));
  }
}

function* deleteWorksheetRowCall(
  action: ReturnType<typeof deleteWorksheetRow>
) {
  try {
    const { row } = action.payload;
    const scenario: PlainProgramScenario = yield select(selectCurrentScenario);
    const worksheet: PlainWorksheet = yield select(selectWorksheet);
    const { rows } = worksheet;
    let newRows = [];

    if (
      ((scenario.isDetailed &&
        row.category === INDIVIDUAL_WORKSPACES_CATEGORY &&
        row.subcategory) ||
        isStorageSupportSpace(row?.projectSpaceType?.spaceType)) &&
      !row?.child
    ) {
      let childRows = isStorageSupportSpace(row.projectSpaceType.spaceType)
        ? worksheet.rowsByCategory[row.category] || []
        : worksheet.rowsBySubcategory[row.subcategory] || [];

      childRows = childRows.filter(
        (childRow: WorksheetRow) =>
          childRow.child &&
          (childRow.spaceType as IChildWorksheetSpaceType).spaceTypeId ===
            row.projectSpaceType.spaceType.id
      );

      const rowsToDelete: Record<number, WorksheetRow> = [
        row,
        ...childRows,
      ].reduce((map, rowToDelete) => {
        return {
          ...map,
          [rowToDelete.id]: rowToDelete,
        };
      }, {});

      newRows = rows.filter((worksheetRow: WorksheetRow) => {
        return isNil(rowsToDelete[worksheetRow.id]);
      });
    } else {
      newRows = rows.filter(
        (worksheetRow: WorksheetRow) => worksheetRow.id !== row?.id
      );
    }

    yield put(populateWorksheet(Worksheet.recalculate(worksheet, newRows)));
    yield put(saveWorksheetAction());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* addRowsToProjectWorksheetsCall(
  action: ReturnType<typeof worksheetActions.addRowsToProjectWorksheets>
) {
  try {
    const {
      spaceTypeCategoryId,
      projectSpaceTypeId,
      childWorksheetSpaceTypeId,
    } = action.payload;

    yield call(addRowsToProjectWorksheetsApi, {
      spaceTypeCategoryId,
      projectSpaceTypeId,
      childWorksheetSpaceTypeId,
    });

    yield put(fetchCurrentWorksheet());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateWorksheetCategoryCall(
  action: ReturnType<typeof worksheetActions.updateWorksheetCategory>
) {
  try {
    const {
      worksheetId,
      categoryId,
      visible,
      parentCategoryId,
    } = action.payload;

    const worksheet: PlainWorksheet = yield select(selectWorksheet);

    const {
      data: { worksheetCategory },
    }: APIResponse<typeof updateWorksheetCategoryApi> = yield call(
      updateWorksheetCategoryApi,
      {
        worksheetId,
        categoryId,
        visible,
      }
    );

    const { categories } = worksheet;
    const updatedCategoryIndex = categories.findIndex(
      (category) => category.categoryId === worksheetCategory.categoryId
    );
    let newCategories = replaceAtIndex(
      categories,
      worksheetCategory,
      updatedCategoryIndex
    );

    const { rows } = worksheet;

    let newRows: WorksheetRow[] = rows;

    if (!visible) {
      const allCategories: IProjectSpaceTypeCategory[] = yield select(
        selectAllProjectSpaceTypeCategories
      );

      const spaceTypeCategory: IProjectSpaceTypeCategory = allCategories.find(
        (pstc: IProjectSpaceTypeCategory) =>
          pstc.id === worksheetCategory.categoryId
      );

      const defaultCategory =
        spaceTypeCategory?.defaultSpaceTypeCategory?.category;

      newRows = (rows || []).filter((row) => {
        if (spaceTypeCategory?.parentId) {
          return row.subcategory !== defaultCategory;
        }
        return row.category !== defaultCategory;
      });
    } else if (parentCategoryId) {
      // show parent category if subcategory is made visible
      const updatedParentCategoryIndex = categories.findIndex(
        (category) => category.categoryId === parentCategoryId
      );
      newCategories = replaceAtIndex(
        newCategories,
        {
          categoryId: parentCategoryId,
          worksheetId,
          visible: true,
        },
        updatedParentCategoryIndex
      );
    }

    const updatedWorksheet = new Worksheet(
      Worksheet.toAPIWorksheet({
        ...worksheet,
        categories: newCategories,
        rows: newRows,
      }),
      worksheet.scenario,
      worksheet.projectSettings
    ).toPlainObject();

    yield put(populateWorksheet(updatedWorksheet));
    if (!parentCategoryId) yield put(fetchCurrentWorksheet());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(
      FETCH_SCENARIOS_ASSOCIATED_WITH_SELECTED_CATEGORY,
      fetchScenariosAssociatedWithSelectedCategoryCall
    ),
    takeLatest(
      FETCH_SCENARIOS_ASSOCIATED_WITH_SELECTED_ROW,
      fetchScenariosAssociatedWithSelectedRow
    ),
    takeLatest(DELETE_SPACE_TYPE_FROM_PROJECT, deleteSpaceTypeFromProject),
    takeLatest(
      DELETE_CHILD_SPACE_TYPE_FROM_PROJECT,
      deleteChildSpaceTypeFromProject
    ),
    takeLatest(
      DELETE_CUSTOM_SUBCATEGORY_SPACE_TYPES,
      deleteCustomSubcategorySpaceTypes
    ),
    takeLatest(
      DELETE_CATEGORY_ROWS_FROM_PROJECT,
      deleteCategoryRowsFromProjectCall
    ),
    takeLatest(
      DELETE_SPACE_TYPE_ROWS_FROM_PROJECT,
      deleteSpaceTypeRowsFromProject
    ),
    takeLatest(
      DELETE_CHILD_WORKSHEET_SPACE_ROWS_FROM_PROJECT,
      deleteChildWorksheetSpaceRowsFromProject
    ),
    takeLatest(
      UPDATE_CHILD_SPACE_TYPE_POSITION,
      updateChildSpaceTypePositionCall
    ),
    takeEvery(DELETE_WORKSHEET_ROW, deleteWorksheetRowCall),
    takeLatest(ADD_ROWS_TO_PROJECT_WORKSHEETS, addRowsToProjectWorksheetsCall),
    takeEvery(UPDATE_WORKSHEET_CATEGORY, updateWorksheetCategoryCall),
  ]);
}

export default saga;
